import { updateCreditsDisplay } from './credits';

function autoSaveReflections() {
  const wrapper = document.getElementById('analysis-actions');
  if (!wrapper) return;
  const sessionId = wrapper.dataset.sessionId;
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

  const payload = {};
  document.querySelectorAll('.js-reflection').forEach(function(el) {
    payload[el.dataset.field] = el.value;
  });

  fetch(`/consultation_sessions/${sessionId}/reflection`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(payload)
  }).catch(function(err) { console.error('Reflection save error:', err); });
}

function buildAnalysisPdf() {
  const wrapper = document.getElementById('analysis-actions');
  if (!wrapper) return;
  const data = JSON.parse(wrapper.dataset.payload);

  // Tolerant of a field not being on the page: the reflection prompts have
  // been a single box and a three-part cycle at different times.
  function reflectionValue(field) {
    const el = document.querySelector('[data-field="' + field + '"]');
    return el ? el.value : '';
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const now = new Date();
  const dateStr = now.toLocaleString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const margin = 18;
  const pageW = 210;
  const contentW = pageW - margin * 2;
  const lineH = 5.5;
  let y = margin;

  function checkPage(needed) {
    needed = needed || lineH;
    if (y + needed > 285) { doc.addPage(); y = margin; }
  }

  function addText(text, opts) {
    opts = opts || {};
    const fontSize = opts.fontSize || 10.5;
    const bold = opts.bold || false;
    const color = opts.color || [30, 30, 30];
    const indent = opts.indent || 0;
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(text == null ? '' : text), contentW - indent);
    lines.forEach(function(line) {
      checkPage();
      doc.text(line, margin + indent, y);
      y += lineH;
    });
  }

  function addSection(title) {
    y += 3;
    checkPage(10);
    doc.setFillColor(220, 230, 245);
    doc.rect(margin, y - 3.5, contentW, 7.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 50, 90);
    doc.text(title, margin + 2, y + 0.5);
    y += lineH + 1;
    doc.setTextColor(30, 30, 30);
  }

  function addLabelledBlock(label, text) {
    addText(label, { bold: true, fontSize: 10.5 });
    addText(text || '(none)', { indent: 4, color: [60, 60, 60] });
    y += 2;
  }

  doc.setFillColor(30, 50, 90);
  doc.rect(0, 0, 210, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text('GP Consultation Analysis', margin, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(200, 210, 230);
  doc.text('Generated: ' + dateStr, margin, 16);
  y = 28;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(30, 30, 30);
  doc.text('Scenario:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.scenarioName || '-', margin + 22, y);
  y += lineH + 2;

  addSection('Learning Objective & Reflections');
  addLabelledBlock('Learning Objective', data.learningObjective);
  addLabelledBlock('Reflection', reflectionValue('reflection_what'));

  if (data.feedback) {
    addSection('Feedback');
    addText(data.feedback, { color: [50, 50, 50] });
  }

  // Left out entirely when an admin has hidden it from users, so the PDF
  // never carries what the page does not show.
  if (data.showDetailedFeedback !== false) {
    addSection('Detailed Feedback');
    let category = null;
    (data.competencies || []).forEach(function(c) {
      if (c.category !== category) {
        category = c.category;
        y += 3;
        checkPage(16);
        addText(category, { bold: true, fontSize: 11.5, color: [30, 50, 90] });
      }
      y += 2;
      checkPage(16);
      addText(c.name, { bold: true });
      if (c.insufficient) {
        addText('Insufficient evidence to analyse', { indent: 4, color: [110, 110, 110] });
        return;
      }
      if (c.evidence) addText('Evidence: ' + c.evidence, { indent: 4, color: [40, 80, 50] });
      if (c.improvement) addText('Improvement: ' + c.improvement, { indent: 4, color: [100, 60, 20] });
    });
  }

  addSection('Consultation Transcript');
  addText(data.transcript || '(no transcript available)', { color: [40, 40, 40] });

  const stamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
  doc.save(`consultation-analysis-${stamp}.pdf`);
}

function pollAnalysisStatus() {
  const wrapper = document.getElementById('analysis-actions');
  if (!wrapper || wrapper.dataset.poll !== 'true') return;

  const sessionId = wrapper.dataset.sessionId;

  const poll = function() {
    fetch(`/consultation_sessions/${sessionId}/status`)
      .then(function(response) { return response.json(); })
      .then(function(data) {
        updateCreditsDisplay(data.credits_remaining);

        if (data.status === 'pending' || data.status === 'processing') {
          setTimeout(poll, 3000);
        } else if (data.status === 'completed') {
          showAnalysisResults(data);
        } else {
          // A failed analysis has nothing to view; reload so the retry button
          // is on the page.
          window.location.reload();
        }
      })
      .catch(function() { setTimeout(poll, 5000); });
  };

  setTimeout(poll, 3000);
}

// A failed analysis is usually a transient API error, so offer another go
// rather than making the trainee run the consultation again.
function initRetryAnalysis() {
  const retryBtn = document.getElementById('btn-retry-analysis');
  if (!retryBtn) return;

  retryBtn.addEventListener('click', function() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    retryBtn.disabled = true;
    retryBtn.textContent = 'Retrying…';

    fetch(retryBtn.dataset.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }
    })
      .then(function(response) { return response.json().catch(function() { return {}; }); })
      .then(function(data) {
        // Reloading picks up the pending state, which brings back the status
        // bar and restarts polling.
        if (data.success) { window.location.reload(); return; }
        throw new Error(data.error || 'Could not restart the analysis.');
      })
      .catch(function(err) {
        retryBtn.disabled = false;
        retryBtn.textContent = 'Retry analysis';
        const message = document.getElementById('retry-analysis-error');
        if (message) {
          message.textContent = err.message;
          message.classList.remove('d-none');
        }
      });
  });
}

// Puts a finished analysis on the page the moment the poll sees it, rather than
// reloading: a reload would throw away a reflection the trainee is still typing.
// The status response carries the same feedback card the page renders itself.
function showAnalysisResults(data) {
  const wrapper = document.getElementById('analysis-actions');
  if (!wrapper) {
    window.location.reload();
    return;
  }

  const feedback = document.getElementById('feedback-summary');
  if (feedback && data.feedback) {
    feedback.textContent = data.feedback;
    feedback.classList.remove('text-muted');
  } else if (feedback) {
    document.getElementById('feedback-card').remove();
  }

  if (data.feedback_html && !document.getElementById('competency-feedback') &&
      !document.getElementById('reveal-diagnosis')) {
    wrapper.insertAdjacentHTML('beforebegin', data.feedback_html);
  }

  // The Save Reflection PDF reads its content from this payload, which was
  // written before the analysis existed.
  const payload = JSON.parse(wrapper.dataset.payload);
  payload.feedback = data.feedback;
  payload.competencies = data.competencies || [];
  wrapper.dataset.payload = JSON.stringify(payload);
  wrapper.dataset.poll = 'false';

  markAnalysisReady();
}

function markAnalysisReady() {
  const bar = document.getElementById('analysis-status-bar');
  if (!bar) return;

  const target = document.getElementById('feedback-card') ||
    document.getElementById('reveal-diagnosis') ||
    document.getElementById('competency-feedback');

  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'analysis-status-action';

  const icon = document.createElement('i');
  icon.className = 'bi bi-check-circle-fill text-success';

  const label = document.createElement('span');
  label.textContent = 'Analysis complete - click here to see your feedback';

  action.appendChild(icon);
  action.appendChild(label);
  action.addEventListener('click', function() {
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  bar.textContent = '';
  bar.classList.add('is-ready');
  bar.appendChild(action);
}

function initAnalysisPage() {
  const saveBtn = document.getElementById('btn-save-reflection');
  if (!saveBtn) return;

  pollAnalysisStatus();
  initRetryAnalysis();

  let saveTimer = null;
  document.querySelectorAll('.js-reflection').forEach(function(el) {
    el.addEventListener('input', function() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(autoSaveReflections, 800);
    });
  });

  saveBtn.addEventListener('click', function() {
    autoSaveReflections();
    saveBtn.textContent = 'Saving PDF…';
    saveBtn.disabled = true;
    try {
      buildAnalysisPdf();
      saveBtn.textContent = 'Saved!';
    } catch (err) {
      console.error('PDF generation error:', err);
      saveBtn.textContent = 'Error — try again';
    }
    setTimeout(function() {
      saveBtn.textContent = 'Save Reflection (PDF)';
      saveBtn.disabled = false;
    }, 2000);
  });
}

document.addEventListener('turbo:load', initAnalysisPage);
document.addEventListener('DOMContentLoaded', initAnalysisPage);
